import { useEffect } from 'react';
import { Link } from 'react-router';
import { useAppState } from '@/app/useAppState';
import { Button, ButtonLink } from '@/components/ui/Button';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { CHANGELOG } from '@/config/changelog';
import { useI18n } from '@/i18n/context';

const latest = CHANGELOG[0];

/**
 * Shown once on the child's home after an update: what the new version brings.
 * A new family has nothing "new" to discover, so the first visit only
 * remembers the current version.
 */
export function WhatsNewBanner() {
  const { t, l } = useI18n();
  const { settings, updateSettings } = useAppState();
  const seen = settings.seenVersion;

  useEffect(() => {
    if (seen === undefined && latest) void updateSettings({ seenVersion: latest.version });
  }, [seen, updateSettings]);

  if (!latest || seen === undefined || seen === latest.version) return null;
  return (
    <section
      aria-labelledby="whats-new-banner"
      className="flex flex-col gap-3 rounded-[var(--radius-card)] border-4 border-sun-300 bg-sun-100 p-4"
    >
      <h2 id="whats-new-banner" className="flex items-center gap-2 text-lg font-extrabold">
        <span aria-hidden className="emoji">
          🎉
        </span>
        {t('whatsNew.banner', { version: latest.version })}
      </h2>
      <ul className="flex flex-col gap-1">
        {latest.changes.slice(0, 3).map((change, i) => (
          <li key={i} className="flex gap-2">
            <span aria-hidden className="emoji">
              {change.emoji}
            </span>
            {l(change.text)}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <ButtonLink to="/whats-new" variant="sun">
          {t('whatsNew.seeAll')}
        </ButtonLink>
        <Button
          variant="ghost"
          onClick={() => void updateSettings({ seenVersion: latest.version })}
        >
          {t('whatsNew.dismiss')}
        </Button>
      </div>
    </section>
  );
}

/** Every release, newest first, in plain words for families. */
export function WhatsNewScreen() {
  const { t, l, formatDate } = useI18n();
  const { settings, updateSettings } = useAppState();

  useEffect(() => {
    if (latest && settings.seenVersion !== latest.version)
      void updateSettings({ seenVersion: latest.version });
  }, [settings.seenVersion, updateSettings]);

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader title={t('whatsNew.title')} subtitle={t('whatsNew.intro')} back={true} />
      <ol className="flex flex-col gap-4">
        {CHANGELOG.map((entry, index) => (
          <li
            key={entry.version}
            className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]"
          >
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-extrabold">
              {t('whatsNew.version', { version: entry.version })}
              <span className="text-sm font-bold text-ink-muted">
                {formatDate(entry.date, { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {index === 0 && (
                <span className="rounded-full bg-sun-300 px-2 py-0.5 text-xs">
                  {t('whatsNew.latest')}
                </span>
              )}
            </h2>
            <ul className="flex flex-col gap-2">
              {entry.changes.map((change, i) => (
                <li key={i} className="flex gap-3">
                  <span aria-hidden className="emoji text-2xl leading-7">
                    {change.emoji}
                  </span>
                  <span>{l(change.text)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
      <p className="text-ink-muted">
        {t('whatsNew.voteHint')}{' '}
        <Link to="/parent/vote" className="font-bold text-sky-700 underline underline-offset-4">
          {t('whatsNew.voteLink')}
        </Link>
      </p>
    </div>
  );
}
